import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import React, { lazy, Suspense } from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import ShowErrorsProvider from "src/utils/providers/ShowErrorsProvider";
import AuthenticationManager from "src/components/profile/authentication-manager";
import AlertDialogDAC7 from "./components/AlertDialogDAC7/AlertDialogDAC7";
import { RouteProcessManager } from "./components/RouteProcessManager/RouteProcessManager";
import { HELP_CATEGORIES_ROUTE, ROUTE_PATHS } from "./utils/constants";
import { RouteInitializerProvider } from "./utils/providers/RouteInitializerProvider";
import { CartProvider } from "./CartProvider";
import PageLayout from "src/components/PageLayout/PageLayout";

// import ProductDetailManager from "./pages/ProductDetail/ProductDetailManager";
// import MySalesManager from "./pages/MySales/MySalesManager";
// import NewSellFormManager from "./pages/NewSelll/SellForm/SellFormManager/NewSellFormManager";
// import OrderDetailManager from "./pages/OrderDetail/OrderDetailManager";


// Lazy loaded components
//
const SellerProfileManager = lazy(() => import("./pages/SellerProfile/SellerProfileManager"),);
const ProductDetailManager = lazy(() => import("./pages/ProductDetail/ProductDetailManager"),);
const PageNotFound = lazy(() => import("./pages/PageNotFound/PageNotFound"));
const MyItemsManager = lazy(() => import("./pages/MyItems/MyItemsManager"));
const BankAccountManager = lazy(() => import("./pages/BankAccount/BankAccountManager"),);
const MyPersonalInfoManager = lazy(() => import("./pages/DAC7/MyPersonalInfo/MyPersonalInfoManager"),);
const StatementManager = lazy(() => import("./pages/DAC7/Statement/StatementManager"),);
const DisputeWS = lazy(() => import("./pages/Dispute/DisputeWS"));
const HelpPageManager = lazy(() => import("./pages/Help/HelpPageManager"));
const BuyingGuide = lazy(() => import("./pages/Help/sections/BuyingGuide"));
const SellingGuide = lazy(() => import("./pages/Help/sections/SellingGuide"));
const SenderAddressManager = lazy(() => import("./pages/MyAddresses/SenderAddress/ReturnAddressManager"),);
const MyFavoritesManager = lazy(() => import("./pages/MyFavorites/MyFavoritesManager"),);
const MyPurchasesManager = lazy(() => import("./pages/MyPurchases/MyPurchasesManager"),);
const MySalesManager = lazy(() => import("./pages/MySales/MySalesManager"));
const OrderDetailManager = lazy(() => import("./pages/OrderDetail/OrderDetailManager"),);
const ProfileManager = lazy(() => import("./pages/Profile/ProfileManager"));
const SearchManager = lazy(() => import("./pages/Search/SearchManager"));
const ImageCrop = lazy(() => import("./pages/Search/ImageCrop/ImageCrop"));
const SellConfirmationManager = lazy(() =>import("./pages/NewSelll/SellConfirmation/SellConfirmationManager"),);
const ReturnAddressListWM = lazy(() => import("./pages/MyAddresses/SenderAddressListWM/ReturnAddressListWM"),);
const MyAddressesManager = lazy(() => import("./pages/MyAddresses/MyAddressesManager"),);
const SenderAddressListManager = lazy(() =>import("./pages/MyAddresses/SenderAddressList/SenderAddressListManager"),);
const NicknameManager = lazy(() => import("./pages/Nickname/NicknameManager"));
const ReportProductManager = lazy(() => import("./pages/ReportProduct/ReportProductManager"),);
const BundleGridManager = lazy(() => import("./pages/BundleGrid/common/BundleGridManager"),);
const SellIntroManager = lazy(() => import("./pages/NewSelll/SellIntro/SellIntroManager"),);
const SellProductDetailManager = lazy(() => import("./pages/NewSelll/SellProductDetail/SellProductDetailManager"),);
const SellResultsManager = lazy(() => import("./pages/NewSelll/SellResults/SellResultsManager"),);
const SellScan = lazy(() => import("./pages/NewSelll/SellScan/SellScan")); // este es el input
const NewSellFormManager = lazy(() => import("./pages/NewSelll/SellForm/SellFormManager/NewSellFormManager"),);
const OriginalBundlePDPManager = lazy(() => import("./pages/BundleGrid/BundleCatalogPDP/OriginalBundlePDPManager"),);
const AskForDetailsContent = lazy(() => import("./pages/AskForDetails/AskForDetailsContent"),);
const CommunicationManager = lazy(() => import("./pages/Communication/CommunicationManager"),);
const CounterOfferManager = lazy(() =>import("./pages/Communication/Variants/CounterOffer/CounterOfferManager"),);
const ManualBarcodeSearchManager = lazy(() => import("./pages/ManualBarcodeSearch/ManualBarcodeSearchManager"),);
const ScanResults = lazy(() => import("./pages/NewSelll/SellResults/ScanResults"),);
const ProductsManager = lazy(() => import("./pages/Products/ProductsManager"));
const CartWS = lazy(() =>import("./pages/Cart/CartWS").then((module) => ({ default: module.CartWS })),);
const ShoppingCart = lazy(() =>import("./pages/ShoppingCart/ShoppingCart").then((module) => ({default: module.ShoppingCart,})),);
const OfferPrice = lazy(() =>import("./pages/OfferPrice/OfferPrice"));
export const routes = (
  <RouteInitializerProvider>
    <Suspense fallback={<></>}>
      <ShowErrorsProvider>
        <AuthenticationManager>
        <CartProvider>
          <AlertDialogDAC7 variant="PRINCIPAL" />
          <RouteProcessManager />
          <Suspense fallback={<></>}>
            <Switch>
              <Route
                path={`${ROUTE_PATHS.PRODUCTS}/:universe?/:id?/:modalId?`}
                component={ProductsManager}
              />
              <Route
                path={`${ROUTE_PATHS.SELLER_PROFILE}/:id`}
                component={SellerProfileManager}
              />
              <Route
                path={ROUTE_PATHS.PAGE_NOT_FOUND}
                component={PageNotFound}
              />
              <Route
                path={`${ROUTE_PATHS.PRODUCT_DETAIL}/:id/:state?`}
                component={ProductDetailManager}
              />
              <Route
                path={ROUTE_PATHS.MY_ITEMS}
                exact
                component={MyItemsManager}
              />
              <Route
                path={ROUTE_PATHS.PRODUCT_CREATION_CONFIRMATION}
                exact
                component={SellConfirmationManager}
              />
              <Route
                path={ROUTE_PATHS.SEARCH_IMAGE_CROP}
                exact
                component={ImageCrop}
              />
              <Route
                path={`${ROUTE_PATHS.SEARCH}/:universe?`}
                exact
                component={SearchManager}
              />
              <Route
                path={`${ROUTE_PATHS.PRODUCT_SEARCH}/:universe?`}
                exact
                component={ProductsManager}
              />
              <Route path={ROUTE_PATHS.CART} exact component={ShoppingCart} />
              <Route
                path={`${ROUTE_PATHS.CHECKOUT}/:stage`}
                exact
                component={ShoppingCart}
              />
              <Route
                path={[
                  `${ROUTE_PATHS.ADDRESS_LIST}/:type`,
                  `${ROUTE_PATHS.PROFILE}${ROUTE_PATHS.ADDRESS_LIST}/:type`,
                ]}
                exact
                component={SenderAddressListManager}
              />
              <Route
                path={`${ROUTE_PATHS.PROFILE}${ROUTE_PATHS.MY_ADDRESSES}`}
                exact
                component={MyAddressesManager}
              />
              <Route
                path={`${ROUTE_PATHS.PROFILE}${ROUTE_PATHS.MY_ADDRESSES}${ROUTE_PATHS.SENDER_ADDRESS}`}
                exact
                component={SenderAddressManager}
              />
              <Route
                path={`${ROUTE_PATHS.PROFILE}${ROUTE_PATHS.RETURN_ADDRESS_LIST__WM}`}
                exact
                component={ReturnAddressListWM}
              />
              <Route
                path={ROUTE_PATHS.PROFILE}
                exact
                component={ProfileManager}
              />
              <Route
                path={ROUTE_PATHS.BANK_ACCOUNT}
                exact
                component={BankAccountManager}
              />
              <Route
                path={ROUTE_PATHS.MY_SALES}
                exact
                component={MySalesManager}
              />
              <Route
                path={`${ROUTE_PATHS.MY_SALES}/:saleOrderId`}
                exact
                component={OrderDetailManager}
              />
              <Route
                path={ROUTE_PATHS.MY_PURCHASES}
                exact
                component={MyPurchasesManager}
              />
              <Route
                path={`${ROUTE_PATHS.MY_PURCHASES}/:purchaseOrderId`}
                exact
                component={OrderDetailManager}
              />
              <Route
                path={`${ROUTE_PATHS.MY_PURCHASES}/:purchaseOrderId/dispute/:expeditionId`}
                exact
                component={DisputeWS}
              />
              <Route path={`${ROUTE_PATHS.PRODUCTS}#cart`} component={CartWS} />
              <Route
                path={ROUTE_PATHS.HELP}
                exact
                component={HelpPageManager}
              />
              <Route
                path={HELP_CATEGORIES_ROUTE.SELLING_URL}
                exact
                component={SellingGuide}
              />
              <Route
                path={HELP_CATEGORIES_ROUTE.BUYING_URL}
                exact
                component={BuyingGuide}
              />
              <Route
                path={`${ROUTE_PATHS.ASK_FOR_DETAILS}/:id`}
                exact
                component={AskForDetailsContent}
              />
              <Route
                path={ROUTE_PATHS.MY_FAVORITES}
                exact
                component={MyFavoritesManager}
              />
              <Route
                path={ROUTE_PATHS.MY_PERSONAL_INFO}
                exact
                component={MyPersonalInfoManager}
              />
              <Route
                path={ROUTE_PATHS.STATEMENT_DAC7}
                exact
                component={StatementManager}
              />
              <Route
                path={ROUTE_PATHS.NICKNAME}
                exact
                component={NicknameManager}
              />
              <Route
                path={`${ROUTE_PATHS.REPORT}/:id`}
                exact
                component={ReportProductManager}
              />
              <Route
                path={[
                  `${ROUTE_PATHS.BUNDLE}${ROUTE_PATHS.PRODUCTS}/:id`,
                  `${ROUTE_PATHS.BUNDLE}${ROUTE_PATHS.SEARCH}/:id`,
                ]}
                exact
                component={BundleGridManager}
              />
              <Route
                path={[
                  `${ROUTE_PATHS.BUNDLE}/:id${ROUTE_PATHS.PRODUCT_DETAIL}/:id`,
                ]}
                exact
                component={OriginalBundlePDPManager}
              />
              <Route
                path={[
                  ROUTE_PATHS.SELL_SCAN_INTRO,
                  ROUTE_PATHS.SELL_SEARCH_INTRO,
                ]}
                exact
                component={SellIntroManager}
              />
              <Route
                path={ROUTE_PATHS.SELL_SCAN_NEW}
                exact
                component={SellScan}
              />
              <Route
                path={[
                  `${ROUTE_PATHS.SELL_RESULTS_SCAN}`,
                  `${ROUTE_PATHS.SELL_RESULTS_SEARCH}`,
                ]}
                exact
                component={SellResultsManager}
              />
              <Route
                path={`${ROUTE_PATHS.SELL_RESULTS_SCAN}/:barcode?`}
                exact
                component={ScanResults}
              />
              <Route
                path={[
                  `${ROUTE_PATHS.SELL_SCAN_PDP_NEW}/:id`,
                  `${ROUTE_PATHS.SELL_SEARCH_PDP_NEW}/:id`,
                ]}
                exact
                component={SellProductDetailManager}
              />
              <Route
                path={[
                  `${ROUTE_PATHS.SELL_FORM_NEW}/:id?`,
                  `${ROUTE_PATHS.SELL_FORM_EDIT}/:id?`,
                ]}
                exact
                component={NewSellFormManager}
              />
              <Route
                path={`${ROUTE_PATHS.REPORT}/:id`}
                exact
                component={ReportProductManager}
              />
              <Route
                path={`${ROUTE_PATHS.COMMUNICATION}/:id`}
                exact
                component={CommunicationManager}
              />
              <Route
                path={`${ROUTE_PATHS.COUNTER_OFFER}/:id`}
                exact
                component={CounterOfferManager}
              />
              <Route
                path={`${ROUTE_PATHS.SELL_SCAN_NEW}${ROUTE_PATHS.MANUAL_BARCODE_SEARCH}`}
                exact
                component={ManualBarcodeSearchManager}
              />
              <Route
                path={`${ROUTE_PATHS.OFFER_PRICE}/:id`}
                exact
                component={OfferPrice}
              />
              <Route
                path="/page-layout"
                exact
                component={PageLayout}
              />
              <Route render={() => <Redirect to={ROUTE_PATHS.PAGE_NOT_FOUND} />} />
            </Switch>
          </Suspense>
        </CartProvider>
      </AuthenticationManager>
    </ShowErrorsProvider>
    </Suspense>
    <ReactQueryDevtools initialIsOpen={false} />
  </RouteInitializerProvider>
);
